import React, { useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import { CurrentRefinementsConnectorParamsRefinement } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import { useCurrentRefinements, useRange } from 'react-instantsearch-hooks';
import { useProductList } from '../../context';
import useRefinementHelpers from '../../hooks/useRefinementHelpers';

// Added ISNZCOMPLAINT
import {
  CAPACITY_SIZE,
  LITRES_PER_MINUTE,
  PAN_HEIGHT,
  PAN_PROJECTION,
  RAIL_LENGTH,
  SHOWER_HEAD_SIZE,
} from '@/helpers/constants/attributes';
import { localeMapping } from '@/helpers/constants/localeMapping';
import { useFormat } from '@/helpers/hooks/useFormat';
import usePath from '@/hooks/usePath';
import useI18n from '@/helpers/hooks/useI18n';

const BUCKETS = {
  'pan height': [
    { label: '<395 mm', min: -Infinity, max: 395 },
    { label: '396–419 mm', min: 396, max: 419 },
    { label: '420–439 mm', min: 420, max: 439 },
    { label: '>440 mm', min: 440, max: Infinity },
  ],
  'pan projection': [
    { label: '<550 mm', min: -Infinity, max: 550 },
    { label: '551–650 mm', min: 551, max: 650 },
    { label: '651–700 mm', min: 651, max: 700 },
    { label: '>700 mm', min: 701, max: Infinity },
  ],
};

const getNumericValue = (value: string) => {
  const match = String(value ?? '').match(/-?\d+(?:\.\d+)?/);

  return match ? Number(match[0]) : NaN;
};

/*
 * Pan Height / Pan Projection are bucketed facets, so a single chip
 * stands for several underlying refinements.
 */
const getAttributeBuckets = (attribute?: string) => {
  const normalized = String(attribute || '').toLowerCase();

  if (normalized.includes('pan-height')) return BUCKETS['pan height'];
  if (normalized.includes('pan-projection')) return BUCKETS['pan projection'];

  return undefined;
};

const CurrentRefinements = () => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  const { locale } = useParams();

  const { numericRanges, removeRefinement, pricesConfiguration } = useProductList();

  const { currency } = useI18n();
  const localeKey = localeMapping[locale];
  const path = usePath();

  const { items } = useCurrentRefinements();

  const { resolveLabel } = useRefinementHelpers();

  const [outletAttribute, setOutletAttribute] = useState('')

  /*
   * A refined value that no longer matches the other active filters comes
   * back with `count: 0` (algoliasearch-helper back-fills it). It is still an
   * applied refinement, so bucketed facets have to keep it - dropping it makes
   * the bucket chip remove only part of what the bucket selected.
   */
  const nonNumericRefinements = useMemo(
    () =>
      items
        .map((item) =>
          item.refinements.filter(
            (refinement) =>
              refinement.type !== 'numeric' &&
              (refinement.count !== 0 || !!getAttributeBuckets(refinement.attribute)),
          ),
        )
        .flat(),
    [items],
  );

  const configuration = useMemo(() => pricesConfiguration[outletAttribute], [pricesConfiguration, outletAttribute]);

  const numericRefinements = useMemo(() => {
    const refinements = items
      .map((item) => item.refinements.filter((refinement) => refinement.type === 'numeric'))
      .flat();
      setOutletAttribute(refinements?.[0]?.attribute)
    const refinementsMap = refinements.reduce<Record<string, [number, number]>>(
      (acc, curr) => ({
        ...acc,
        [curr.attribute]: [
          curr.operator?.includes('>') ? +curr.value : acc[curr.attribute]?.[0] ?? numericRanges[curr.attribute]?.[0],
          curr.operator?.includes('<') ? +curr.value : acc[curr.attribute]?.[1] ?? numericRanges[curr.attribute]?.[1],
        ],
      }),
      {}
    );

   const finalRefinements = Object.entries(refinementsMap).map<CurrentRefinementsConnectorParamsRefinement>(
  ([attribute, range]) =>
    ({
      type: 'numeric',
      attribute,
      label: `${(range[0] || 0) + 'mm' + ' -'} ${
        range[1]
          ? range[1] + 'mm'
          : configuration?.ranges?.[
                configuration?.ranges.length - 1
              ]?.max + 'mm'
      }`,
    } as CurrentRefinementsConnectorParamsRefinement),
);

return finalRefinements;
  }, [
    items,
    numericRanges,
    currency,
    locale,
  ]);
  /*
   * Group selected Pan Height / Pan Projection
   * refinements into buckets for display only.
   */
 const bucketRefinements = useMemo(() => {
  const result: any[] = [];

  nonNumericRefinements.forEach((refinement) => {
    const buckets = getAttributeBuckets(refinement.attribute);

    if (!buckets) {
      result.push({
        type: 'normal',
        refinement,
      });
      return;
    }

    const rawValue = String(
      refinement.value ?? refinement.label ?? '',
    );

    const numericValue = getNumericValue(rawValue);

    if (Number.isNaN(numericValue)) {
      result.push({
        type: 'normal',
        refinement,
      });
      return;
    }

    const bucket = buckets.find(
      ({ min, max }) =>
        numericValue >= min && numericValue <= max,
    );

    if (!bucket) {
      result.push({
        type: 'normal',
        refinement,
      });
      return;
    }

    const existing = result.find(
      (item) =>
        item.type === 'bucket' &&
        item.attribute === refinement.attribute &&
        item.bucket.label === bucket.label,
    );

    if (existing) {
      existing.refinements.push(refinement);
    } else {
      result.push({
  type: 'bucket',
  attribute: refinement.attribute,
  bucket,
  refinements: [refinement],
});
    }
  });

  return result;
}, [nonNumericRefinements]);

  if (!nonNumericRefinements.length && !numericRefinements.length) return <></>;

  const typeMap =
    nonNumericRefinements.reduce<
      Record<string, string>
    >((acc, x) => {
      const attribute = x.attribute;
      const value = x.value;

      if (typeof attribute === 'string') {
        if (
          path.path.includes('brands') &&
          attribute.includes(
            `categories.${localeKey}.lvl1`,
          )
        ) {
          acc[attribute] = 'Sub Category';
        } else if (
          path.path.includes('brands') &&
          attribute.includes(
            `categories.${localeKey}.lvl0`,
          )
        ) {
          acc[attribute] = 'Category';
        } else if (
          attribute.includes('categories')
        ) {
          acc[attribute] = 'categories';
        } else if (
          attribute.includes('range')
        ) {
          acc[attribute] = 'Collection';
        } else if (
          attribute.includes('colour')
        ) {
          acc[attribute] = 'Colour';
        } else if (
          attribute.includes('product-type')
        ) {
          acc[attribute] =
            typeof value === 'string' &&
            value.includes('Grab Rails')
              ? 'Categories'
              : 'Product Type';
        } else if (
          attribute.includes('item-material')
        ) {
          acc[attribute] = 'Material';
        } else if (
          attribute.includes('trap-type')
        ) {
          acc[attribute] = 'Trap Type';
        } else if (
          attribute.includes('inlet-type')
        ) {
          acc[attribute] = 'Inlet Type';
        } else if (
          attribute.includes('outlet-length')
        ) {
          acc[attribute] = 'Outlet Length';
        } else if (
          attribute.includes('outlet-type')
        ) {
          acc[attribute] = 'Outlet Type';
        } else if (
          attribute.includes('pan-projection')
        ) {
          acc[attribute] = 'Pan Projection';
        } else if (
          attribute.includes('clean-flush')
        ) {
          acc[attribute] = 'Clean Flush';
        } else if (
          attribute.includes('pan-height')
        ) {
          acc[attribute] = 'Pan Height';
        } else if (
          attribute.includes('category')
        ) {
          acc[attribute] = 'Category';
        } else if (
          attribute.includes(
            'capacity-to-overflow',
          )
        ) {
          acc[attribute] =
            'Capacity to Overflow';
        } else if (
          attribute.includes('capacity-size')
        ) {
          acc[attribute] = 'Capacity Size';
        } else if (
          attribute.includes('shape')
        ) {
          acc[attribute] = 'Shape';
        } else if (
          attribute.includes('tap-holes')
        ) {
          acc[attribute] = 'Tapholes';
        } else if (
          attribute.includes(
            '.wels-with-flow-rate-flush-volume-1',
          )
        ) {
          acc[attribute] = 'WELS Rating';
        } else if (
          attribute.includes('overflow')
        ) {
          acc[attribute] = 'Overflow';
        } else if (
          attribute.includes('litres-per-minute')
        ) {
          acc[attribute] =
            'Litres Per Minute';
        } else if (
          attribute.includes('IsAS14281Compliant')
        ) {
          acc[attribute] =
            'AS1428.1 Accessible';
        } else if (
          attribute.includes('IsAS14281Ambulant')
        ) {
          acc[attribute] =
            'AS1428.1 Ambulant';
        } else if (
          attribute.includes(
            'IsIndependentLivingCompliant',
          )
        ) {
          acc[attribute] =
            'Independent Living Compliant';
        } else if (
          attribute.includes('cartridge-type')
        ) {
          acc[attribute] = 'Cartridge Type';
        } else if (
          attribute.includes('cartidge-size')
        ) {
          acc[attribute] = 'Cartridge Size';
        } else if (
          attribute.includes('surface-finish')
        ) {
          acc[attribute] = 'Surface Finish';
        } else if (
          attribute.includes('waste-position')
        ) {
          acc[attribute] = 'Waste Position';
        } else if (
          attribute.includes('spray-function')
        ) {
          acc[attribute] = 'Spray Function';
        } else if (
          attribute.includes('rail-length')
        ) {
          acc[attribute] = 'Rail Length';
        } else if (
          attribute.includes(
            'shower-head-diameter',
          )
        ) {
          acc[attribute] =
            'Shower Head Size';
        } else if (
          attribute.includes(
            'wels-with-flow-rate-flush-volume-2',
          )
        ) {
          acc[attribute] = 'WELS Rating';
        } else if (
          attribute.includes('mount-type')
        ) {
          acc[attribute] = 'Mount Type';
        } else if (
          attribute.includes('bowl-position')
        ) {
          acc[attribute] =
            'Bowl Position';
        } else if (
          attribute.includes('IsNZ41121Compliant')
        ) {
          acc[attribute] =
            'NZ4121.1 Accessible';
        } else if (
          attribute.includes('is-retrofit')
        ) {
          acc[attribute] = 'Retrofit';
        } else {
          acc[attribute] =
            attribute
              ?.split('.')
              ?.pop()
              ?.replaceAll('-', ' ') || '';
        }
      }

      return acc;
    }, {});

  const numericTypeMap =
    numericRefinements.reduce<
      Record<string, string>
    >((acc, x) => {
      const attribute = x.attribute;

      if (typeof attribute === 'string') {
        if (
          attribute.includes('outlet-length')
        ) {
          acc[attribute] = 'Outlet Length';
        } else if (
          attribute.includes('outlet-type')
        ) {
          acc[attribute] = 'Outlet Type';
        } else if (
          attribute.includes('pan-projection')
        ) {
          acc[attribute] = 'Pan Projection';
        } else if (
          attribute.includes('clean-flush')
        ) {
          acc[attribute] = 'Clean Flush';
        } else if (
          attribute.includes('pan-height')
        ) {
          acc[attribute] = 'Pan Height';
        }
      }

      return acc;
    }, {});

  const combinedTypeMap = {
    ...typeMap,
    ...numericTypeMap,
  };

  return (
    <div className="mt-[10px] flex flex-wrap items-center justify-start gap-[8px] px-[24px] md:px-[28px] lg:gap-[14px] lg:px-0 xl:px-0 2xl:px-0">
      {[
        ...numericRefinements,
        ...bucketRefinements,
      ].map((refinement: any) => {
        /*
         * Bucket refinement
         */
        if (refinement.type === 'bucket') {
          return (
            <div
              key={`${refinement.attribute}-${refinement.bucket.label}`}
              className="flex cursor-default items-center justify-center gap-[8px] rounded border border-neutral-500 bg-white px-[8px] py-[6px] transition hover:border-primary-black"
            >
              <span className="text-[12px] leading-[20px] text-secondary-black 2xl:text-[14px]">
                <span className="capitalize">{combinedTypeMap[refinement.attribute]}</span>: {refinement.bucket.label}
              </span>

              <CloseIcon
                className="w-[16px] cursor-pointer fill-secondary-black stroke-0 2xl:w-[20px]"
                onClick={() => {
                  refinement.refinements.forEach(
                    (item: any) =>
                      removeRefinement(item),
                  );
                }}
              />
            </div>
          );
        }

        /*
         * Normal / numeric refinement
         */
        const currentRefinement =
          refinement.type === 'normal'
            ? refinement.refinement
            : refinement;

        return (
          <div
            key={currentRefinement.value}
            className="flex cursor-default items-center justify-center gap-[8px] rounded border border-neutral-500 bg-white px-[8px] py-[6px] transition hover:border-primary-black"
          >
            <span className="text-[12px] leading-[20px] text-secondary-black 2xl:text-[14px] capitalize">
              {combinedTypeMap[
                currentRefinement.attribute
              ]}{' '}
              :{' '}
              {currentRefinement.attribute
                .split('.')
                .pop() === PAN_HEIGHT ||
              currentRefinement.attribute
                .split('.')
                .pop() === PAN_PROJECTION ||
              currentRefinement.attribute
                .split('.')
                .pop() === RAIL_LENGTH ||
              currentRefinement.attribute
                .split('.')
                .pop() ===
                SHOWER_HEAD_SIZE
                ? `${resolveLabel(
                    currentRefinement.attribute,
                    currentRefinement.label,
                  )
                    ?.split('>')
                    ?.pop()}mm`
                : currentRefinement.attribute
                    .split('.')
                    .pop() ===
                  LITRES_PER_MINUTE
                ? `${resolveLabel(
                    currentRefinement.attribute,
                    currentRefinement.label,
                  )
                    ?.split('>')
                    ?.pop()}L/min`
                : currentRefinement.attribute
                    .split('.')
                    .pop() ===
                  CAPACITY_SIZE
                ? `${resolveLabel(
                    currentRefinement.attribute,
                    currentRefinement.label,
                  )
                    ?.split('>')
                    ?.pop()}L`
                : resolveLabel(
                    currentRefinement.attribute,
                    currentRefinement.label,
                  )
                    ?.split('>')
                    ?.pop()
                    ?.replace('true', 'Yes')
                    ?.replace('false', 'No')}
            </span>

            <CloseIcon
              className="w-[16px] cursor-pointer fill-secondary-black stroke-0 2xl:w-[20px]"
              onClick={() =>
                removeRefinement(
                  currentRefinement,
                )
              }
            />
          </div>
        );
      })}
    </div>
  );
};

export default CurrentRefinements;
